import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Ticket, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { TicketPreview } from './TicketPreview';
import { FeaturedNumberPicker } from './FeaturedNumberPicker';
import { DigitBoxInput } from '../ui/DigitBoxInput';
import { Random10PackEditor } from './Random10PackEditor';
import { useWallet } from '../../context/WalletContext';
import { lotteryService } from '../../services/lotteryService';
import { ticketsService } from '../../services/ticketsService';
import { formatMoney } from '../../utils/formatMoney';
import {
  confirmPurchase,
  getApiMessage,
  showError,
  showSuccess,
} from '../../utils/swal';
import {
  TICKET_TABS,
  BET_TYPES,
  BET_TYPE_LABELS,
  TICKET_NUMBER_LENGTH,
  RANDOM_PACK_SIZE,
  getTicketCountByBetType,
  generateUniqueRandomNumbers,
} from '../../utils/ticketNumberUtils';

const TAB_CONFIG = [
  { id: TICKET_TABS.NORMAL, label: 'ปกติ', digitLength: TICKET_NUMBER_LENGTH },
  { id: TICKET_TABS.THREE_DIGIT, label: '3 ตัว', digitLength: 3 },
  { id: TICKET_TABS.TWO_DIGIT, label: '2 ตัว', digitLength: 2 },
  { id: TICKET_TABS.RANDOM_10, label: 'สุ่ม 10 ใบ', digitLength: 6 },
];

function BetTypeSelector({ betType, onChange, suffix, digitLength }) {
  const straightCount = 1;
  const permutationCount =
    suffix.length === digitLength
      ? getTicketCountByBetType(suffix, BET_TYPES.PERMUTATION)
      : 0;

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">รูปแบบการซื้อ</p>
      <div className="grid grid-cols-2 gap-3">
        {[BET_TYPES.STRAIGHT, BET_TYPES.PERMUTATION].map((type) => {
          const count =
            type === BET_TYPES.STRAIGHT ? straightCount : permutationCount;
          const isDisabled =
            type === BET_TYPES.PERMUTATION && suffix.length !== digitLength;
          return (
            <button
              key={type}
              type="button"
              disabled={isDisabled}
              onClick={() => onChange(type)}
              className={`rounded-xl border-2 p-4 text-left transition-all ${
                betType === type
                  ? 'border-brand-red bg-red-50 shadow-sm'
                  : 'border-gray-200 bg-white hover:border-brand-gold'
              } ${isDisabled ? 'cursor-not-allowed opacity-40' : ''}`}
            >
              <p className="font-semibold text-brand-red">
                {BET_TYPE_LABELS[type]}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {type === BET_TYPES.STRAIGHT
                  ? 'ซื้อเลขตรงตามที่กรอก 1 ใบ'
                  : `สลับเลขทุกแบบ (${count || '...'} ใบ)`}
              </p>
            </button>
          );
        })}
      </div>
      {betType === BET_TYPES.PERMUTATION && suffix.length === digitLength && (
        <p className="text-xs text-gray-500">
          โต๊ด {suffix} = {permutationCount} ใบ (สลับเลข {digitLength} หลักท้าย
          ส่วนหน้าสุ่มอัตโนมัติให้ครบ {TICKET_NUMBER_LENGTH} หลัก)
        </p>
      )}
    </div>
  );
}

function findDuplicateIndexes(numbers) {
  const seen = new Map();
  const dups = new Set();
  numbers.forEach((num, index) => {
    if (!num) return;
    if (seen.has(num)) {
      dups.add(index);
      dups.add(seen.get(num));
    } else {
      seen.set(num, index);
    }
  });
  return dups;
}

export function TicketPurchase({ ticketPurchaseRef }) {
  const { balance, refreshAfterPurchase } = useWallet();
  const [activeTab, setActiveTab] = useState(TICKET_TABS.NORMAL);
  const [betType, setBetType] = useState(BET_TYPES.STRAIGHT);
  const [ticketDigits, setTicketDigits] = useState('');
  const [digitTab, setDigitTab] = useState(TICKET_TABS.NORMAL);
  const [packNumbers, setPackNumbers] = useState(() =>
    generateUniqueRandomNumbers(TICKET_NUMBER_LENGTH, RANDOM_PACK_SIZE),
  );
  const [previewNumbers, setPreviewNumbers] = useState([]);
  const [ticketCount, setTicketCount] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [pricePerTicket, setPricePerTicket] = useState(1);
  const [activePeriod, setActivePeriod] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const isNormalTab = activeTab === TICKET_TABS.NORMAL;
  const isRandomTab = activeTab === TICKET_TABS.RANDOM_10;
  const activeTabConfig = TAB_CONFIG.find((t) => t.id === activeTab);
  const digitLength = isRandomTab
    ? digitTab === TICKET_TABS.THREE_DIGIT
      ? 3
      : digitTab === TICKET_TABS.TWO_DIGIT
        ? 2
        : TICKET_NUMBER_LENGTH
    : activeTabConfig.digitLength;

  const duplicateIndexes = useMemo(
    () => (isRandomTab ? findDuplicateIndexes(packNumbers) : new Set()),
    [isRandomTab, packNumbers],
  );

  const isPackComplete =
    isRandomTab &&
    packNumbers.length === RANDOM_PACK_SIZE &&
    packNumbers.every((n) => n.length === digitLength) &&
    duplicateIndexes.size === 0;

  const isInputComplete = isRandomTab
    ? isPackComplete
    : ticketDigits.length === digitLength;
  const hasEnoughBalance = balance >= totalCost;

  useEffect(() => {
    lotteryService.getConfig().then((config) => {
      setPricePerTicket(config.ticketPriceUsd);
    });
    lotteryService.getActivePeriod().then(setActivePeriod);
  }, []);

  useEffect(() => {
    if (!isInputComplete) {
      setPreviewNumbers([]);
      setTicketCount(0);
      setTotalCost(0);
      return undefined;
    }

    setIsPreviewLoading(true);

    const payload = isRandomTab
      ? {
          tab: TICKET_TABS.RANDOM_10,
          digitTab,
          numbers: packNumbers,
          betType:
            digitTab === TICKET_TABS.NORMAL ? BET_TYPES.STRAIGHT : betType,
        }
      : {
          tab: activeTab,
          number: ticketDigits,
          betType: isNormalTab ? BET_TYPES.STRAIGHT : betType,
        };

    let cancelled = false;
    ticketsService
      .preview(payload)
      .then((preview) => {
        if (cancelled) return;
        setPreviewNumbers(preview.ticketNumbers);
        setTicketCount(preview.ticketCount);
        setTotalCost(preview.totalCost);
        setPricePerTicket(preview.pricePerTicket);
      })
      .catch(() => {
        if (cancelled) return;
        setPreviewNumbers([]);
        setTicketCount(0);
        setTotalCost(0);
      })
      .finally(() => {
        if (!cancelled) setIsPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isInputComplete,
    activeTab,
    ticketDigits,
    betType,
    isNormalTab,
    isRandomTab,
    digitTab,
    packNumbers,
  ]);

  const rerollPack = (nextDigitTab = digitTab) => {
    const length =
      nextDigitTab === TICKET_TABS.THREE_DIGIT
        ? 3
        : nextDigitTab === TICKET_TABS.TWO_DIGIT
          ? 2
          : TICKET_NUMBER_LENGTH;
    setPackNumbers(generateUniqueRandomNumbers(length, RANDOM_PACK_SIZE));
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setBetType(BET_TYPES.STRAIGHT);
    setTicketDigits('');
    setPreviewNumbers([]);
    setTicketCount(0);
    setTotalCost(0);
    if (tabId === TICKET_TABS.RANDOM_10) {
      setDigitTab(TICKET_TABS.NORMAL);
      setPackNumbers(
        generateUniqueRandomNumbers(TICKET_NUMBER_LENGTH, RANDOM_PACK_SIZE),
      );
    }
  };

  const handleDigitTabChange = (next) => {
    setDigitTab(next);
    setBetType(BET_TYPES.STRAIGHT);
    rerollPack(next);
  };

  const handleChangePackNumber = (index, next) => {
    setPackNumbers((current) => {
      const copy = [...current];
      copy[index] = next;
      return copy;
    });
  };

  const buildPurchasePayload = () => {
    if (isRandomTab) {
      return {
        tab: TICKET_TABS.RANDOM_10,
        digitTab,
        numbers: packNumbers,
        betType:
          digitTab === TICKET_TABS.NORMAL ? BET_TYPES.STRAIGHT : betType,
      };
    }
    return {
      tab: activeTab,
      number: ticketDigits,
      betType: isNormalTab ? BET_TYPES.STRAIGHT : betType,
    };
  };

  const handlePurchase = async () => {
    if (!isInputComplete) {
      await showError({
        title: 'กรอกเลขไม่ครบ',
        text: isRandomTab
          ? 'กรุณากรอกเลข 10 ใบให้ครบและไม่ซ้ำกัน'
          : `กรุณากรอกเลข ${digitLength} หลักให้ครบก่อนซื้อตั๋ว`,
      });
      return;
    }
    if (isRandomTab && duplicateIndexes.size > 0) {
      await showError({
        title: 'เลขซ้ำ',
        text: 'เลขในชุดสุ่ม 10 ใบต้องไม่ซ้ำกัน',
      });
      return;
    }
    if (!hasEnoughBalance) {
      await showError({
        title: 'ยอดไม่พอ',
        text: 'ยอดเงินไม่เพียงพอ กรุณาฝากเงิน',
      });
      return;
    }

    const confirmed = await confirmPurchase({
      title: 'ยืนยันซื้อตั๋ว',
      html: `<p>ซื้อ <strong>${ticketCount}</strong> ใบ</p><p class="mt-1">ยอดรวม <strong>${formatMoney(totalCost)}</strong></p>`,
      confirmText: `ซื้อ ${ticketCount} ใบ`,
    });
    if (!confirmed) return;

    setIsPurchasing(true);

    const result = await ticketsService
      .purchase(buildPurchasePayload())
      .catch(async (error) => {
        await showError({
          title: 'ซื้อไม่สำเร็จ',
          text: getApiMessage(error, 'ไม่สามารถซื้อตั๋วได้'),
        });
        return null;
      });

    if (result) {
      refreshAfterPurchase(result.balance, result.tickets);
      await showSuccess({
        title: 'ซื้อสำเร็จ',
        text: result.message || `ซื้อตั๋ว ${result.ticketCount} ใบสำเร็จ!`,
      });
      setTicketDigits('');
      setPreviewNumbers([]);
      setTicketCount(0);
      setTotalCost(0);
      if (isRandomTab) {
        rerollPack();
      }
    }

    setIsPurchasing(false);
  };

  const buyLabel = (() => {
    if (ticketCount > 0) {
      return `ซื้อ ${ticketCount} ใบ — ${formatMoney(totalCost)}`;
    }
    if (isRandomTab) return 'ซื้อสุ่ม 10 ใบ';
    return `ซื้อตั๋ว (${digitLength} หลัก${isNormalTab ? '' : 'ท้าย'})`;
  })();

  return (
    <div ref={ticketPurchaseRef}>
      <Card>
        <CardHeader className="bg-gradient-to-r from-brand-red to-brand-red-dark">
          <CardTitle className="flex items-center gap-2 text-white">
            <Ticket className="h-5 w-5 text-brand-gold" />
            ซื้อตั๋ว Mungkorn Lotto
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex flex-wrap rounded-xl border border-red-100 bg-red-50/30 p-1">
            {TAB_CONFIG.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`min-w-[22%] flex-1 rounded-lg px-1 py-2.5 text-xs font-semibold transition-colors sm:text-sm ${
                  activeTab === tab.id
                    ? 'bg-brand-red text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-brand-red'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activePeriod && (
            <div className="rounded-xl border border-red-100 bg-red-50/50 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4 text-brand-red" />
                <span>งวดที่ {activePeriod.period}</span>
                <span className="text-gray-400">|</span>
                <span>
                  ออกรางวัล{' '}
                  {new Date(activePeriod.drawDate).toLocaleDateString('th-TH')}
                </span>
              </div>
            </div>
          )}

          {isRandomTab ? (
            <Random10PackEditor
              digitTab={digitTab}
              onDigitTabChange={handleDigitTabChange}
              betType={betType}
              onBetTypeChange={setBetType}
              numbers={packNumbers}
              onChangeNumber={handleChangePackNumber}
              onReroll={() => rerollPack()}
              duplicateIndexes={duplicateIndexes}
            />
          ) : isNormalTab ? (
            <FeaturedNumberPicker
              selectedNumber={ticketDigits}
              onSelectNumber={setTicketDigits}
              manualInput={ticketDigits}
              onManualInputChange={setTicketDigits}
            />
          ) : (
            <div className="space-y-4">
              <DigitBoxInput
                id="suffix-digits"
                length={digitLength}
                value={ticketDigits}
                onChange={setTicketDigits}
                label={`กรอกเลข ${digitLength} หลักท้าย`}
              />
              <p className="text-xs text-gray-500">
                เลขหน้าจะถูกสุ่มอัตโนมัติให้ครบ {TICKET_NUMBER_LENGTH} หลัก
              </p>
              <BetTypeSelector
                betType={betType}
                onChange={setBetType}
                suffix={ticketDigits}
                digitLength={digitLength}
              />
            </div>
          )}

          {isPreviewLoading && (
            <p className="text-center text-sm text-gray-500">
              กำลังสร้างตัวอย่างตั๋ว...
            </p>
          )}

          {previewNumbers.length > 0 && !isPreviewLoading && (
            <TicketPreview
              ticketNumbers={previewNumbers}
              ticketCount={previewNumbers.length}
              title={
                isRandomTab
                  ? `ตัวอย่างตั๋วสุ่ม (${ticketCount} ใบ)`
                  : isNormalTab
                    ? 'เลขตั๋วของคุณ'
                    : `ตัวอย่างตั๋ว (${BET_TYPE_LABELS[betType]})`
              }
            />
          )}

          <div className="grid grid-cols-2 gap-3 rounded-xl bg-gray-50 p-4 text-sm">
            <div>
              <p className="text-gray-500">ราคาต่อใบ</p>
              <p className="font-semibold">{formatMoney(pricePerTicket)}</p>
            </div>
            <div>
              <p className="text-gray-500">
                รวม {ticketCount > 0 ? `${ticketCount} ใบ` : ''}
              </p>
              <p className="text-lg font-bold text-brand-gold">
                {formatMoney(totalCost)}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-brand-gold/50 bg-brand-gold-light/20 px-4 py-3">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-brand-gold" />
              <span className="text-sm text-gray-600">ยอดในกระเป๋า</span>
            </div>
            <span className="text-lg font-bold text-brand-red">
              {formatMoney(balance)}
            </span>
          </div>

          {!hasEnoughBalance && isInputComplete && ticketCount > 0 && (
            <div className="rounded-lg border border-brand-red/30 bg-red-50 p-3 text-center text-sm text-brand-red">
              ยอดเงินไม่เพียงพอ{' '}
              <Link to="/wallet" className="font-semibold underline">
                ฝากเงินที่นี่
              </Link>
            </div>
          )}

          <Button
            size="lg"
            className="w-full"
            disabled={
              isPurchasing ||
              isPreviewLoading ||
              !isInputComplete ||
              ticketCount === 0 ||
              !hasEnoughBalance
            }
            onClick={handlePurchase}
          >
            {buyLabel}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
